import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from './ui/button';
import { Loader2, X, RotateCw } from 'lucide-react';

/**
 * Crop an image (data URL or remote URL) to a square based on pixel area from react-easy-crop.
 * Returns a Blob.
 */
async function getCroppedBlob(imageSrc, croppedAreaPixels, rotation = 0) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });

  const radians = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  const bBoxWidth = image.width * cos + image.height * sin;
  const bBoxHeight = image.width * sin + image.height * cos;

  // Rotate canvas
  const rotCanvas = document.createElement('canvas');
  rotCanvas.width = bBoxWidth;
  rotCanvas.height = bBoxHeight;
  const rotCtx = rotCanvas.getContext('2d');
  rotCtx.translate(bBoxWidth / 2, bBoxHeight / 2);
  rotCtx.rotate(radians);
  rotCtx.drawImage(image, -image.width / 2, -image.height / 2);

  // Crop canvas
  const canvas = document.createElement('canvas');
  canvas.width = croppedAreaPixels.width;
  canvas.height = croppedAreaPixels.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    rotCanvas,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
  });
}

export default function ImageCropper({ imageSrc, aspect = 1, onClose, onConfirm, busy = false }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onCropComplete = useCallback((_, area) => {
    setCroppedAreaPixels(area);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, rotation);
      await onConfirm(blob);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" data-testid="image-cropper-modal">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/5 hover:bg-white/10 active:scale-95 transition"
          data-testid="cropper-close-btn"
        >
          <X className="w-5 h-5 text-white" />
        </button>
        <p className="text-sm font-medium text-white font-['Unbounded']">Adjust Photo</p>
        <button
          onClick={() => setRotation((r) => (r + 90) % 360)}
          className="p-2 rounded-full bg-white/5 hover:bg-white/10 active:scale-95 transition"
          data-testid="cropper-rotate-btn"
          title="Rotate"
        >
          <RotateCw className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Crop area */}
      <div className="relative flex-1">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspect}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
          objectFit="contain"
          showGrid={false}
        />
      </div>

      {/* Controls */}
      <div className="p-4 space-y-4 border-t border-white/10 bg-[#0A0A0A]">
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-[#71717A]">Zoom</p>
          <input
            type="range"
            min="1"
            max="3"
            step="0.01"
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-[#E11D48]"
            data-testid="cropper-zoom-slider"
          />
        </div>
        <Button
          onClick={handleConfirm}
          disabled={!croppedAreaPixels || processing || busy}
          className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white py-5 rounded-2xl font-bold"
          data-testid="cropper-confirm-btn"
        >
          {processing || busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...
            </>
          ) : (
            'Apply'
          )}
        </Button>
      </div>
    </div>
  );
}
